import axios, { AxiosInstance } from "axios";
import { ServiceConfig, TokenStorage } from "../types";
import { HttpService } from "../services/http-service";
import { ServiceFactory } from "../factories/service-factory";
import { AuthInterceptor } from "../interceptors/auth-interceptor";
import { ErrorInterceptor } from "../interceptors/error-interceptor";
import { TokenRefreshService } from "../services/token-refresh-service";
import { CookieTokenStorage } from "../storage/token-storage";
import { DEFAULT_SERVICES, DEFAULT_HEADERS } from "../config/default-config";

export class ApiClient {
  private serviceFactory: ServiceFactory;
  private authInterceptor: AuthInterceptor;
  private errorInterceptor: ErrorInterceptor;
  private tokenRefreshService: TokenRefreshService;
  private apiInstances: Map<string, AxiosInstance> = new Map();

  constructor(
    services: Record<string, ServiceConfig> = DEFAULT_SERVICES,
    tokenStorage: TokenStorage = new CookieTokenStorage()
  ) {
    this.serviceFactory = new ServiceFactory();
    this.authInterceptor = new AuthInterceptor(tokenStorage);

    // Initialize token refresh service with auth service endpoint
    const authConfig = services.auth;
    this.tokenRefreshService = new TokenRefreshService(
      tokenStorage,
      `${authConfig.baseURL}/auth/refresh`
    );

    this.errorInterceptor = new ErrorInterceptor(
      tokenStorage,
      this.tokenRefreshService
    );

    this.initializeServices(services);
    this.setupErrorInterceptor();
  }

  private initializeServices(services: Record<string, ServiceConfig>): void {
    Object.entries(services).forEach(([serviceName, config]) => {
      const axiosInstance = this.createAxiosInstance(config);
      const httpService = new HttpService(axiosInstance);

      this.apiInstances.set(serviceName, axiosInstance);
      this.serviceFactory.register(serviceName, httpService);
    });
  }

  private createAxiosInstance(config: ServiceConfig): AxiosInstance {
    const instance = axios.create({
      baseURL: config.baseURL || "http://localhost:3000/api",
      timeout: config.timeout || 10000,
      headers: {
        ...DEFAULT_HEADERS,
        ...config.headers,
      },
    });

    // Add interceptors
    instance.interceptors.request.use(this.authInterceptor.request);
    instance.interceptors.response.use(
      (response) => response,
      this.errorInterceptor.response
    );

    return instance;
  }

  private setupErrorInterceptor(): void {
    this.errorInterceptor.setApiFinder((config: unknown) => {
      for (const [, instance] of this.apiInstances) {
        if (
          (config as Record<string, unknown>).baseURL ===
          instance.defaults.baseURL
        ) {
          return instance;
        }
      }

      const fallback = this.apiInstances.values().next().value;
      if (!fallback) {
        throw new Error("No AxiosInstance found for error interceptor.");
      }

      return fallback;
    });
  }

  // Public API methods
  public getService(serviceName: string): HttpService {
    return this.serviceFactory.get(serviceName);
  }

  public hasService(serviceName: string): boolean {
    return this.serviceFactory.has(serviceName);
  }

  public getAllServices(): string[] {
    return Array.from(this.serviceFactory.getAll().keys());
  }

  // Convenience methods
  public get auth(): HttpService {
    return this.getService("auth");
  }

  public get web(): HttpService {
    return this.getService("web");
  }
}
